import { Divider, Flex } from "antd"
import { colors, spacings } from "themes/variables"
import IconWithBg from "ui/IconWithBg"
import { Text } from "ui/Typography"
import styles from "../index.module.css"
import moment from "moment"
import { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { getEventBorderColor, getEventColor, getJournalIcon, hasEventConflict } from "../../../utils"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import EventBlock from "../EventBlock"
import Journal from "../../../Modals/Journal"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffForDate } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import API from "constants/api"
import { useSelector } from "react-redux"

const EQUIPMENT_API = `${API.BASE_URL}/guide/boats/listing?lang=en-GB`

const getResourceEvents = (dayEvents, resource, isEquipmentTab, isCrewTab, resourceId, resourceLabel) => {
  if (isEquipmentTab) {
    return dayEvents.filter((ev) =>
      (ev.assigned_equipment || []).some((item) => item?.name === resource || item?.equipment_title === resource)
    )
  }
  if (isCrewTab) {
    if (resource === "Unassigned") return dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0)
    return dayEvents.filter((ev) =>
      (ev.assigned_crew || []).some((item) => item?.name === resource || item?.full_name === resource)
    )
  }
  if (resourceId) return dayEvents.filter((ev) => ev.experience_id === resourceId)
  return dayEvents.filter((ev) => ev.resource === resourceLabel)
}

const renderSlotEvents = (resourceEvents, hour, isMultiple, setIsBookingModalOpen, setSelectedBookingId, timeOffForDay) => {
  const slotEvents = resourceEvents.filter(
    (ev) => ev.startHour !== null && ev.endHour !== null && ev.startHour <= hour && ev.endHour > hour
  )
  if (!slotEvents.length) return null
  const multiple = slotEvents.length > 1
  return (
    <div style={{ display: "flex", gap: 4, width: "100%", minWidth: 0, minHeight: multiple ? 100 : undefined }}>
      {slotEvents.map((ev) => (
        <div key={ev.id || ev.title + ev.start + ev.end} style={{ flex: 1, minWidth: 0 }}>
          <EventBlock
            event={ev}
            eventColor={getEventColor(ev.payment_status)}
            eventBorderColor={getEventBorderColor(ev.payment_status)}
            conflict={hasEventConflict(ev.startMoment, ev.endMoment, timeOffForDay)}
            setIsBookingModalOpen={setIsBookingModalOpen}
            setSelectedBookingId={setSelectedBookingId}
            fullHeight
            multipleInSlot={multiple}
          />
        </div>
      ))}
    </div>
  )
}

const DayGrid = ({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab }) => {
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])
  const isCrewTab = activeTab === 'crew';
  const isEquipmentTab = activeTab === 'equipment';
  const crewMembers = useMemo(() => {
    if (!isCrewTab) return []
    const active = calendarCrew.filter((c) => c.status?.toLowerCase() === "active")
    if (profileIds?.length > 0) {
      const filteredIds = profileIds.filter((id) => id !== "unassigned").map(String)
      if (filteredIds.length > 0) return active.filter((c) => filteredIds.includes(String(c.id)))
    }
    return active
  }, [calendarCrew, profileIds, isCrewTab])

  // Fetch equipment list when equipment tab is active
  const [equipmentList, setEquipmentList] = useState([]);
  useEffect(() => {
    if (!isEquipmentTab) return;
    const token = localStorage.getItem('access_token');
    const fetchOptions = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    fetch(EQUIPMENT_API, fetchOptions)
      .then((r) => r.json())
      .then((res) => {
        const items = (res.data || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }));
        setEquipmentList(items);
      })
      .catch(() => {});
  }, [isEquipmentTab]);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const displayDate = date ? moment(date).format("dddd, D MMMM") : ""
  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  const currentDay = moment(date).format("YYYY-MM-DD")
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const hasJournalNote = journalNoteDates?.includes(currentDay)
  // Show icon if journal exists and user has view or edit permission, or if no journal and user has create permission
  const hasJournalPermission = hasJournalNote ? (canViewJournal || canEditJournal) : canCreateJournal
  // Convert event times to 24-hour format for matching
  const normalizedDayEvents = events.map((ev) => {
    const startMoment = ev.start ? moment(ev.start, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    const endMoment = ev.end ? moment(ev.end, ["h:mm A", "hh:mm A", "HH:mm"]) : null
    return {
      ...ev,
      resource: ev.resource || ev.title || "Experience",
      startMoment,
      endMoment,
      startHour: startMoment?.isValid() ? startMoment.hour() : null,
      endHour: endMoment?.isValid() ? endMoment.hour() : null,
      startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
      endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end
    }
  })

  const dayEvents = normalizedDayEvents.filter((ev) => moment(ev.date).format("YYYY-MM-DD") === currentDay)

  const resources = useMemo(() => {
    if (isCrewTab) {
      return ["Unassigned", ...crewMembers.map((c) => c.name)]
    }
    if (isEquipmentTab) {
      const list = equipmentList.map((eq) => eq.name)
      if (boatIds?.length > 0) return equipmentList.filter((eq) => boatIds.includes(eq.id)).map((eq) => eq.name)
      return list
    }
    const explicit = (resourcesProp || []).filter(Boolean)
    if (explicit.length) {
      // Deduplicate by id to preserve experiences with same title
      const encounteredIds = new Set()
      return explicit.filter((item) => {
        const key = item?.id ?? item
        if (encounteredIds.has(key)) return false
        encounteredIds.add(key)
        return true
      })
    }
    const names = normalizedDayEvents.map((ev) => ev.resource).filter(Boolean)
    const unique = Array.from(new Set(names))
    return unique.length ? unique : ["Experience"]
  }, [normalizedDayEvents, resourcesProp, isCrewTab, isEquipmentTab, equipmentList, crewMembers, boatIds])

  const columnCount = resources.length || 1;
  const gridTemplateColumns = useMemo(
    () => `64px 1px repeat(${columnCount}, minmax(160px, 1fr))`,
    [columnCount]
  )

  const showCurrentTimeLine = moment(date).isSame(moment(), "day")
  const currentTimeOffset = useMemo(() => {
    if (!showCurrentTimeLine) return null
    const startHour = hours?.[0] ?? 0
    const now = moment()
    const hourValue = now.hour() + now.minute() / 60
    const rowHeight = 80
    const rawOffset = (hourValue - startHour) * rowHeight
    const maxOffset = hours.length * rowHeight
    return Math.max(0, Math.min(rawOffset, maxOffset))
  }, [hours, showCurrentTimeLine])

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  // Centralized time-off info via hook
  const timeOffForDay = useTimeOffForDate(date)

  return (
    <>
      <Flex vertical>
        <Flex gap={spacings.XS} align="center" className="pt-m">
          <Text color={colors.textSecondaryDark}>{displayDate} Journal</Text>
          {hasJournalPermission && (
            <IconWithBg
              icon={getJournalIcon(journalNoteDates, currentDay)}
              onClick={active ? handleJournalClick : undefined}
              disabled={!active}
            />
          )}
        </Flex>
        <Divider />
        <div key={`grid-${columnCount}`} className={styles.timeGridRoot}>
          <div className={styles.timeGridHeaderRow} style={{ gridTemplateColumns }}>
            <div className={styles.timeLabelHeader} />
            <div className={styles.timeDividerHeader} />
            {resources.map((resource) => {
              const label = typeof resource === "string" ? resource : resource?.title || resource?.name || resource?.trip_title || resource?.label
              const key = typeof resource === "string" ? resource : resource?.id ?? label
              const displayLabel = label
              return (
                <div key={key} className={styles.resourceHeaderCell}>
                  <Text fw="500">{displayLabel}</Text>
                </div>
              )
            })}
          </div>
          <div className={styles.timeGridBody}>
            {showCurrentTimeLine && currentTimeOffset !== null && (
              <div className={styles.currentTimeLine} style={{ top: `${currentTimeOffset}px` }}>
                <span className={styles.currentTimeDot} />
              </div>
            )}
            <div className={styles.timeGridRows} style={{ gridTemplateRows: `repeat(${hours.length}, 1fr)` }}>
              {hours.map((hour) => {
                const now = moment()
                const dayMoment = moment(date)
                const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())

                return (
                  <div key={hour} className={styles.timeGridRow} style={{ gridTemplateColumns }}>
                    <div className={styles.timeLabel}>
                      <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                    </div>
                    <div className={styles.timeDivider} />
                    {resources.map((resource) => {
                      const resourceId = typeof resource === "string" ? null : resource?.id
                      const resourceLabel = typeof resource === "string" ? resource : resource?.title || resource?.name || resource?.trip_title || resource?.label
                      const resourceKey = typeof resource === "string" ? resource : resource?.id ?? resourceLabel
                      const resourceEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId, resourceLabel)
                      const isInEvent = resourceEvents.some((ev) => ev.startHour < hour && ev.endHour > hour)

                      if (isInEvent && !resourceEvents.some((ev) => ev.startHour === hour)) {
                        return (
                          <div
                            key={`${resourceKey}-${hour}`}
                            className={`${styles.eventCell} ${styles.dayEventCell} ${isPastHour ? styles.pastCalendarCell : ""}`}
                          />
                        )
                      }

                      return (
                        <div
                          key={`${resourceKey}-${hour}`}
                          className={`${styles.eventCell} ${styles.dayEventCell} ${isPastHour ? styles.pastCalendarCell : ""}`}
                        >
                          {(timeOffForDay?.ranges || []).map((toff) => {
                            const start = toff.start.hour() + toff.start.minute() / 60
                            const end = toff.end.hour() + toff.end.minute() / 60
                            if (start < hour + 1 && end > hour) {
                              const top = Math.max(0, (start - hour) * 100)
                              const height = (Math.min(end, hour + 1) - Math.max(start, hour)) * 100
                              return (
                                <div
                                  key={`${currentDay}-${resourceKey}-${toff.start.format("HH:mm")}-${toff.end.format("HH:mm")}`}
                                  className={styles.timeOffOverlay}
                                  style={{ top: `${top}%`, height: `${height}%` }}
                                />
                              )
                            }
                            return null
                          })}
                          {renderSlotEvents(resourceEvents, hour, false, setIsBookingModalOpen, setSelectedBookingId, timeOffForDay)}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Flex>
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={date}
          startDate={date}
          endDate={date}
        />
      )}
      {isBookingModalOpen && (
        <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} onCancelSuccess={handleBookingCancelSuccess} />
      )}
    </>
  )
}

DayGrid.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  resources: PropTypes.array,
  activeTab: PropTypes.string,
  profileIds: PropTypes.array,
  boatIds: PropTypes.array,
}

export default DayGrid
